import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Shield, TimerReset } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuthStore } from "../store/admin-auth.store";

interface VerifyOtpLocationState {
	phone?: string;
	from?: string;
}

export function AdminVerifyOtpPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const hasRequestedRef = useRef(false);
	const { phone = "", from } =
		(location.state as VerifyOtpLocationState | null) || {};
	const {
		operator,
		error,
		isLoading,
		otpSent,
		pendingPhone,
		sendOtp,
		verifyOtp,
		clearError,
		clearSession,
		hasStoredSession,
		resetOtpState,
	} = useAdminAuthStore();
	const hasSession = hasStoredSession();
	const [otp, setOtp] = useState(["", "", "", "", "", ""]);
	const [resendTimer, setResendTimer] = useState(30);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	useEffect(() => {
		if (operator && !hasSession) {
			clearSession();
		}
	}, [clearSession, hasSession, operator]);

	useEffect(() => {
		if (!phone) {
			navigate("/login", { replace: true });
			return;
		}

		if (hasRequestedRef.current) {
			return;
		}

		hasRequestedRef.current = true;

		if (!otpSent || pendingPhone !== phone) {
			// No send completed for this phone -- a refresh, a direct link, or
			// stale state. The login page's "Continue" click is now the only
			// place that sends an OTP; go back and let the user request one
			// explicitly instead of silently sending another here (this used to
			// burn the shared rate limit on every remount).
			navigate("/login", { replace: true, state: { phone } });
		}
	}, [navigate, otpSent, pendingPhone, phone]);

	useEffect(() => {
		if (resendTimer > 0) {
			const timer = window.setTimeout(() => {
				setResendTimer((value) => value - 1);
			}, 1000);
			return () => window.clearTimeout(timer);
		}
	}, [resendTimer]);

	if (operator && hasSession) {
		return <Navigate to={from || "/payments"} replace />;
	}

	const handleOtpChange = (index: number, value: string) => {
		clearError();

		const nextValue = value.replace(/\D/g, "").slice(-1);
		const nextOtp = [...otp];
		nextOtp[index] = nextValue;
		setOtp(nextOtp);

		if (nextValue && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}

		if (nextOtp.every(Boolean) && nextOtp.join("").length === 6) {
			void handleVerify(nextOtp.join(""));
		}
	};

	const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
		if (event.key === "Backspace" && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (event: React.ClipboardEvent) => {
		event.preventDefault();
		const pastedValue = event.clipboardData
			.getData("text")
			.replace(/\D/g, "")
			.slice(0, 6);

		if (pastedValue.length !== 6) {
			return;
		}

		const nextOtp = pastedValue.split("");
		setOtp(nextOtp);
		void handleVerify(pastedValue);
	};

	const handleVerify = async (otpValue: string) => {
		try {
			await verifyOtp(phone, otpValue);
			navigate(from || "/payments", { replace: true });
		} catch {
			setOtp(["", "", "", "", "", ""]);
			inputRefs.current[0]?.focus();
		}
	};

	const handleResend = async () => {
		try {
			await sendOtp(phone);
			setResendTimer(30);
			setOtp(["", "", "", "", "", ""]);
			inputRefs.current[0]?.focus();
		} catch {
			// Store handles the visible error state
		}
	};

	const handleBack = () => {
		resetOtpState();
		navigate("/login", {
			replace: true,
			state: { phone, from },
		});
	};

	return (
		<div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(245,158,11,0.18),_transparent_32%),linear-gradient(180deg,_#fcfcfd,_#f3f4f6)] px-4 py-10">
			<div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent" />
			<Card className="w-full max-w-md border-white/70 bg-white/90 shadow-2xl shadow-slate-200/70 backdrop-blur">
				<CardHeader className="gap-3">
					<div className="flex size-12 items-center justify-center rounded-2xl bg-slate-950 text-white">
						<Shield className="size-5" />
					</div>
					<div className="space-y-1">
						<CardTitle className="text-2xl">Verify code</CardTitle>
						<CardDescription>
							Enter the six-digit code for{" "}
							<span className="font-medium">{phone}</span>.
						</CardDescription>
					</div>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="grid gap-4 rounded-2xl border bg-slate-50/80 p-4">
						<div className="flex justify-center gap-2" onPaste={handlePaste}>
							{otp.map((digit, index) => (
								<Input
									// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length OTP input, order is stable
									key={`admin-otp-${index}`}
									ref={(element) => {
										inputRefs.current[index] = element;
									}}
									type="text"
									inputMode="numeric"
									value={digit}
									onChange={(event) =>
										handleOtpChange(index, event.target.value)
									}
									onKeyDown={(event) => handleKeyDown(index, event)}
									className="h-14 w-12 text-center text-xl font-semibold"
									maxLength={1}
									disabled={isLoading}
								/>
							))}
						</div>

						{isLoading && !otpSent ? (
							<p className="text-center text-sm text-slate-500">
								Sending OTP...
							</p>
						) : null}

						{resendTimer > 0 ? (
							<p className="text-center text-sm text-slate-500">
								Resend available in {resendTimer}s
							</p>
						) : (
							<Button
								type="button"
								variant="ghost"
								onClick={() => void handleResend()}
								disabled={isLoading}
								className="justify-between"
							>
								Resend OTP
								<TimerReset data-icon="inline-end" />
							</Button>
						)}
					</div>

					{error ? (
						<div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
							<div className="flex items-start justify-between gap-3">
								<span>{error}</span>
								<button
									type="button"
									onClick={clearError}
									className="font-medium text-rose-700"
								>
									Clear
								</button>
							</div>
						</div>
					) : null}

					<Button
						type="button"
						variant="ghost"
						onClick={handleBack}
						disabled={isLoading}
						className="w-full text-sm text-slate-600 hover:text-slate-900"
					>
						&larr; Change phone number
					</Button>
				</CardContent>
			</Card>
		</div>
	);
}
